// liquid-hover.js  (v4)
// WebGL liquid-distortion hover effect for Shopify Horizon.
//
// v4: OVERSCAN REMOVED. The original component drew into a canvas 120% of the
// element at top/left -10%, and the shader then inset the image by 5/6 so the
// net result was 1:1. That arithmetic only survives if the canvas display size
// is exactly 120%; in a full-bleed Shopify section it resolved to 100% while
// the -10% offset remained, so the image rendered at 1/1.2 = 83% of the width
// and left a white gap on the right. overscanFactor and innerScale are now 1,
// the canvas fills the element exactly, and there is no geometry left to slip.
// Cost: distortion near the frame edge smears (shader clamps its sampling)
// instead of pulling in pixels from outside. Fine for a banner.
//
// v3: canvas display size in percentages, never pixels; re-measure after load.
// v2: render-target format detection with checkFramebufferStatus, static <img>
// fallback instead of a silent blank canvas, blit() buffer leak fixed.
(function () {
  if (typeof window === "undefined" || !("customElements" in window)) return;
  if (customElements.get("liquid-hover")) return;

  class LiquidHover extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      var el = this;
      var src = el.getAttribute("image") || "";
      var alt = el.getAttribute("image-alt") || "";
      var resolution = parseFloat(el.getAttribute("resolution") || "10");
      var cursorSize = parseFloat(el.getAttribute("cursor-size") || "50");
      var intensity = parseFloat(el.getAttribute("intensity") || "50");

      el.style.position = "relative";
      el.style.display = "block";
      el.style.overflow = "hidden";
      if (!el.style.width) el.style.width = "100%";

      var img = document.createElement("img");
      img.className = "liquid-hover__fallback";
      img.alt = alt;
      img.decoding = "async";
      if (src) img.src = src;
      img.style.cssText =
        "position:absolute;top:0;left:0;width:100%;height:100%;" +
        "object-fit:cover;display:block;z-index:0;" +
        "transition:opacity .35s ease;";
      el.appendChild(img);

      function degrade(reason) {
        el.setAttribute("data-liquid-hover", "fallback");
        el.setAttribute("data-liquid-hover-reason", reason);
        if (el._canvas && el._canvas.parentNode) el._canvas.parentNode.removeChild(el._canvas);
        el._canvas = null;
        img.style.opacity = "1";
        if (window.console) console.warn("[liquid-hover] static fallback:", reason);
      }
      el._degrade = degrade;

      if (!src) return degrade("no image set on the block");

      var reduce =
        window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduce) return degrade("prefers-reduced-motion");

      var canvas = document.createElement("canvas");
      canvas.style.cssText =
        "position:absolute;top:0;left:0;width:100%;height:100%;z-index:1;";
      el.appendChild(canvas);
      el._canvas = canvas;

      var self = this;
      requestAnimationFrame(function () {
        try {
          self._cleanup = boot(el, canvas, img, src, resolution, cursorSize, intensity, degrade);
        } catch (e) {
          degrade("init threw: " + (e && e.message ? e.message : e));
        }
      });
    }

    disconnectedCallback() {
      if (this._cleanup) this._cleanup();
      this._cleanup = null;
      this._booted = false;
    }
  }

  function boot(container, canvas, fallbackImg, imageSrc, resolution, cursorSize, intensity, degrade) {
    var opts = {
      alpha: true,
      premultipliedAlpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      preserveDrawingBuffer: false,
    };

    var gl = canvas.getContext("webgl2", opts);
    var isWebGL2 = !!gl;
    if (!gl) {
      gl = canvas.getContext("webgl", opts) || canvas.getContext("experimental-webgl", opts);
    }
    if (!gl) {
      degrade("no WebGL context available");
      return function () {};
    }

    var halfFloatExt = null;
    var linearExt = null;
    var halfFloatType;

    if (isWebGL2) {
      gl.getExtension("EXT_color_buffer_float");
      gl.getExtension("EXT_color_buffer_half_float");
      linearExt = gl.getExtension("OES_texture_float_linear");
      halfFloatType = gl.HALF_FLOAT;
    } else {
      halfFloatExt = gl.getExtension("OES_texture_half_float");
      linearExt = gl.getExtension("OES_texture_half_float_linear");
      if (!halfFloatExt) {
        degrade("OES_texture_half_float unsupported");
        return function () {};
      }
      halfFloatType = halfFloatExt.HALF_FLOAT_OES;
    }

    function supportsTarget(internalFormat, format, type) {
      var tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, 4, 4, 0, format, type, null);
      var fb = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
      var ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.deleteFramebuffer(fb);
      gl.deleteTexture(tex);
      return ok;
    }

    var INTERNAL = isWebGL2 ? gl.RGBA16F : gl.RGBA;
    var FORMAT = gl.RGBA;
    var TYPE = halfFloatType;

    if (!supportsTarget(INTERNAL, FORMAT, TYPE)) {
      var floatOk = false;
      if (isWebGL2) {
        gl.getExtension("EXT_color_buffer_float");
        floatOk = supportsTarget(gl.RGBA32F, gl.RGBA, gl.FLOAT);
        if (floatOk) {
          INTERNAL = gl.RGBA32F;
          TYPE = gl.FLOAT;
        }
      } else if (gl.getExtension("OES_texture_float")) {
        floatOk = supportsTarget(gl.RGBA, gl.RGBA, gl.FLOAT);
        if (floatOk) {
          INTERNAL = gl.RGBA;
          TYPE = gl.FLOAT;
        }
      }
      if (!floatOk) {
        degrade("no renderable float/half-float target");
        return function () {};
      }
    }

    var FILTER = linearExt ? gl.LINEAR : gl.NEAREST;

    var cp = intensity / 100;
    var params = {
      cursorRadiusPx: cursorSize,
      cursorPower: 5 + ((cp - 0.1) * (50 - 5)) / (1 - 0.1),
      distortionPower: intensity / 100,
    };
    // No overscan: canvas maps 1:1 to the element. See the v4 note at the top.
    var overscanFactor = 1;
    var innerScale = 1;
    var pointer = {
      x: 0.65 * container.clientWidth,
      y: 0.5 * container.clientHeight,
      dx: 0,
      dy: 0,
      moved: false,
    };
    var res = { w: 0, h: 0 };
    var outputColor, velocity, divergence, pressure;
    var imageTexture = null;
    var imgRatio = 1;
    var isHovering = false;
    var rafId = null;
    var visible = true;
    var revealed = false;

    var VERT = [
      "precision highp float;",
      "varying vec2 vUv;",
      "attribute vec2 a_position;",
      "varying vec2 vL;",
      "varying vec2 vR;",
      "varying vec2 vT;",
      "varying vec2 vB;",
      "uniform vec2 u_texel;",
      "void main () {",
      "  vUv = .5 * (a_position + 1.);",
      "  vL = vUv - vec2(u_texel.x, 0.);",
      "  vR = vUv + vec2(u_texel.x, 0.);",
      "  vT = vUv + vec2(0., u_texel.y);",
      "  vB = vUv - vec2(0., u_texel.y);",
      "  gl_Position = vec4(a_position, 0., 1.);",
      "}",
    ].join("\n");

    var FRAG_ADVECT = [
      "precision highp float;",
      "precision highp sampler2D;",
      "varying vec2 vUv;",
      "uniform sampler2D u_velocity_texture;",
      "uniform sampler2D u_input_texture;",
      "uniform vec2 u_texel;",
      "uniform vec2 u_output_textel;",
      "uniform float u_dt;",
      "uniform float u_dissipation;",
      "vec4 bilerp (sampler2D sam, vec2 uv, vec2 tsize) {",
      "  vec2 st = uv / tsize - 0.5;",
      "  vec2 iuv = floor(st);",
      "  vec2 fuv = fract(st);",
      "  vec4 a = texture2D(sam, (iuv + vec2(0.5, 0.5)) * tsize);",
      "  vec4 b = texture2D(sam, (iuv + vec2(1.5, 0.5)) * tsize);",
      "  vec4 c = texture2D(sam, (iuv + vec2(0.5, 1.5)) * tsize);",
      "  vec4 d = texture2D(sam, (iuv + vec2(1.5, 1.5)) * tsize);",
      "  return mix(mix(a, b, fuv.x), mix(c, d, fuv.x), fuv.y);",
      "}",
      "void main () {",
      "  vec2 coord = vUv - u_dt * bilerp(u_velocity_texture, vUv, u_texel).xy * u_texel;",
      "  vec4 velocity = bilerp(u_input_texture, coord, u_output_textel);",
      "  gl_FragColor = u_dissipation * velocity;",
      "}",
    ].join("\n");

    var FRAG_DIVERGENCE = [
      "precision highp float;",
      "precision highp sampler2D;",
      "varying highp vec2 vUv;",
      "varying highp vec2 vL;",
      "varying highp vec2 vR;",
      "varying highp vec2 vT;",
      "varying highp vec2 vB;",
      "uniform sampler2D u_velocity_texture;",
      "void main () {",
      "  float L = texture2D(u_velocity_texture, vL).x;",
      "  float R = texture2D(u_velocity_texture, vR).x;",
      "  float T = texture2D(u_velocity_texture, vT).y;",
      "  float B = texture2D(u_velocity_texture, vB).y;",
      "  float div = .25 * (R - L + T - B);",
      "  gl_FragColor = vec4(div, 0., 0., 1.);",
      "}",
    ].join("\n");

    var FRAG_PRESSURE = [
      "precision highp float;",
      "precision highp sampler2D;",
      "varying highp vec2 vUv;",
      "varying highp vec2 vL;",
      "varying highp vec2 vR;",
      "varying highp vec2 vT;",
      "varying highp vec2 vB;",
      "uniform sampler2D u_pressure_texture;",
      "uniform sampler2D u_divergence_texture;",
      "void main () {",
      "  float L = texture2D(u_pressure_texture, vL).x;",
      "  float R = texture2D(u_pressure_texture, vR).x;",
      "  float T = texture2D(u_pressure_texture, vT).x;",
      "  float B = texture2D(u_pressure_texture, vB).x;",
      "  float divergence = texture2D(u_divergence_texture, vUv).x;",
      "  float pressure = (L + R + B + T - divergence) * .25;",
      "  gl_FragColor = vec4(pressure, 0., 0., 1.);",
      "}",
    ].join("\n");

    var FRAG_GRAD_SUB = [
      "precision highp float;",
      "precision highp sampler2D;",
      "varying highp vec2 vUv;",
      "varying highp vec2 vL;",
      "varying highp vec2 vR;",
      "varying highp vec2 vT;",
      "varying highp vec2 vB;",
      "uniform sampler2D u_pressure_texture;",
      "uniform sampler2D u_velocity_texture;",
      "void main () {",
      "  float L = texture2D(u_pressure_texture, vL).x;",
      "  float R = texture2D(u_pressure_texture, vR).x;",
      "  float T = texture2D(u_pressure_texture, vT).x;",
      "  float B = texture2D(u_pressure_texture, vB).x;",
      "  vec2 velocity = texture2D(u_velocity_texture, vUv).xy;",
      "  velocity.xy -= vec2(R - L, T - B);",
      "  gl_FragColor = vec4(velocity, 0., 1.);",
      "}",
    ].join("\n");

    var FRAG_POINT = [
      "precision highp float;",
      "precision highp sampler2D;",
      "varying vec2 vUv;",
      "uniform sampler2D u_input_texture;",
      "uniform float u_ratio;",
      "uniform vec3 u_point_value;",
      "uniform vec2 u_point;",
      "uniform float u_point_size;",
      "void main () {",
      "  vec2 p = vUv - u_point.xy;",
      "  p.x *= u_ratio;",
      "  vec3 splat = .6 * pow(2., -dot(p, p) / u_point_size) * u_point_value;",
      "  vec3 base = texture2D(u_input_texture, vUv).xyz;",
      "  gl_FragColor = vec4(base + splat, 1.);",
      "}",
    ].join("\n");

    var FRAG_OUTPUT = [
      "precision highp float;",
      "precision highp sampler2D;",
      "varying vec2 vUv;",
      "uniform float u_ratio;",
      "uniform float u_img_ratio;",
      "uniform float u_disturb_power;",
      "uniform sampler2D u_output_texture;",
      "uniform sampler2D u_velocity_texture;",
      "uniform sampler2D u_text_texture;",
      "uniform float u_canvas_scale;",
      "uniform float u_inner_scale;",
      "vec2 get_img_uv() {",
      "  vec2 uv = vUv - 0.5;",
      "  uv *= u_canvas_scale;",
      "  uv /= u_inner_scale;",
      "  float containerAspect = u_ratio;",
      "  float imageAspect = u_img_ratio;",
      "  vec2 scale = vec2(1.0);",
      "  if (containerAspect > imageAspect) {",
      "    scale.y = imageAspect / containerAspect;",
      "  } else {",
      "    scale.x = containerAspect / imageAspect;",
      "  }",
      "  uv *= scale;",
      "  return uv + 0.5;",
      "}",
      "vec3 sample_image_smooth(vec2 uv) {",
      "  vec2 uvc = clamp(uv, 0.0, 1.0);",
      "  return texture2D(u_text_texture, vec2(uvc.x, 1.0 - uvc.y)).rgb;",
      "}",
      "void main () {",
      "  float offset = texture2D(u_output_texture, vUv).r;",
      "  vec2 velocity = texture2D(u_velocity_texture, vUv).xy;",
      "  velocity += .001;",
      "  vec2 img_uv = get_img_uv();",
      "  img_uv -= 2.0 * u_disturb_power * normalize(velocity) * offset;",
      "  vec3 img = sample_image_smooth(img_uv);",
      "  gl_FragColor = vec4(img, 1.0);",
      "}",
    ].join("\n");

    function createShader(source, type) {
      var shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        var info = gl.getShaderInfoLog(shader) || "shader compile error";
        gl.deleteShader(shader);
        throw new Error(info);
      }
      return shader;
    }

    function createProgramFromSources(vsSource, fsSource) {
      var program = gl.createProgram();
      gl.attachShader(program, createShader(vsSource, gl.VERTEX_SHADER));
      gl.attachShader(program, createShader(fsSource, gl.FRAGMENT_SHADER));
      gl.bindAttribLocation(program, 0, "a_position");
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program) || "program link error");
      }
      var uniforms = {};
      var n = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
      for (var i = 0; i < n; i++) {
        var active = gl.getActiveUniform(program, i);
        if (!active) continue;
        uniforms[active.name] = gl.getUniformLocation(program, active.name);
      }
      return { program: program, uniforms: uniforms };
    }

    var quadVbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quadVbo);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]),
      gl.STATIC_DRAW
    );
    var quadEbo = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, quadEbo);
    gl.bufferData(
      gl.ELEMENT_ARRAY_BUFFER,
      new Uint16Array([0, 1, 2, 0, 2, 3]),
      gl.STATIC_DRAW
    );
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(0);

    function blit(target) {
      if (target == null) {
        gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      } else {
        gl.viewport(0, 0, target.width, target.height);
        gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
      }
      gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
    }

    function createFBO(w, h) {
      gl.activeTexture(gl.TEXTURE0);
      var texture = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, FILTER);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, FILTER);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D, 0, INTERNAL, w, h, 0, FORMAT, TYPE, null);
      var fbo = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
      if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
        throw new Error("framebuffer incomplete at " + w + "x" + h);
      }
      gl.viewport(0, 0, w, h);
      gl.clear(gl.COLOR_BUFFER_BIT);
      return {
        fbo: fbo,
        texture: texture,
        width: w,
        height: h,
        attach: function (id) {
          gl.activeTexture(gl.TEXTURE0 + id);
          gl.bindTexture(gl.TEXTURE_2D, texture);
          return id;
        },
      };
    }

    function createDoubleFBO(w, h) {
      var fbo1 = createFBO(w, h);
      var fbo2 = createFBO(w, h);
      return {
        width: w,
        height: h,
        texelSizeX: 1 / w,
        texelSizeY: 1 / h,
        read: function () { return fbo1; },
        write: function () { return fbo2; },
        swap: function () { var t = fbo1; fbo1 = fbo2; fbo2 = t; },
        dispose: function () {
          [fbo1, fbo2].forEach(function (f) {
            gl.deleteFramebuffer(f.fbo);
            gl.deleteTexture(f.texture);
          });
        },
      };
    }

    gl.clearColor(0, 0, 0, 0);

    var splatProgram = createProgramFromSources(VERT, FRAG_POINT);
    var divergenceProgram = createProgramFromSources(VERT, FRAG_DIVERGENCE);
    var pressureProgram = createProgramFromSources(VERT, FRAG_PRESSURE);
    var gradientSubtractProgram = createProgramFromSources(VERT, FRAG_GRAD_SUB);
    var advectionProgram = createProgramFromSources(VERT, FRAG_ADVECT);
    var displayProgram = createProgramFromSources(VERT, FRAG_OUTPUT);

    function disposeFBOs() {
      [outputColor, velocity, pressure].forEach(function (d) {
        if (d && d.dispose) d.dispose();
      });
      if (divergence) {
        gl.deleteFramebuffer(divergence.fbo);
        gl.deleteTexture(divergence.texture);
      }
    }

    function initFBOs() {
      disposeFBOs();
      outputColor = createDoubleFBO(res.w, res.h);
      velocity = createDoubleFBO(res.w, res.h);
      divergence = createFBO(res.w, res.h);
      pressure = createDoubleFBO(res.w, res.h);
    }

    function resizeCanvas() {
      var rect = container.getBoundingClientRect();
      var width = Math.max(1, Math.round(rect.width) || container.clientWidth);
      var height = Math.max(1, Math.round(rect.height) || container.clientHeight);
      var dpr = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = Math.max(2, Math.round(width * dpr));
      canvas.height = Math.max(2, Math.round(height * dpr));

      // Fill the element exactly. Percentages, never pixels: CSS may widen the
      // element after this runs and a pixel width would freeze it narrow.
      canvas.style.position = "absolute";
      canvas.style.top = "0";
      canvas.style.left = "0";
      canvas.style.width = "100%";
      canvas.style.height = "100%";

      var ratio = width / height;
      var baseResolution = 128 + ((resolution - 1) * (512 - 128)) / 9;
      res.w = Math.max(2, Math.round(baseResolution * ratio));
      res.h = Math.max(2, Math.round(baseResolution));
    }

    resizeCanvas();
    initFBOs();

    function updatePointerPosition(eX, eY) {
      pointer.moved = true;
      pointer.dx = 6 * (eX - pointer.x);
      pointer.dy = 6 * (eY - pointer.y);
      pointer.x = eX;
      pointer.y = eY;
    }

    var onEnter = function () { isHovering = true; };
    var onLeave = function () { isHovering = false; pointer.moved = false; };
    var onMove = function (e) {
      if (!isHovering) return;
      var rect = container.getBoundingClientRect();
      updatePointerPosition(e.clientX - rect.left, e.clientY - rect.top);
    };
    var onTouchMove = function (e) {
      isHovering = true;
      var t = e.targetTouches[0];
      var rect = container.getBoundingClientRect();
      updatePointerPosition(t.clientX - rect.left, t.clientY - rect.top);
    };
    var onTouchStart = function () { isHovering = true; };
    var onTouchEnd = function () { isHovering = false; pointer.moved = false; };

    var lastW = 0;
    var lastH = 0;
    var onResize = function () {
      var rect = container.getBoundingClientRect();
      var w = Math.round(rect.width);
      var h = Math.round(rect.height);
      if (w === lastW && h === lastH) return;
      lastW = w;
      lastH = h;
      resizeCanvas();
      try { initFBOs(); } catch (e) { degrade("resize: " + e.message); stop(); }
    };

    var onContextLost = function (e) {
      e.preventDefault();
      degrade("webgl context lost");
      stop();
    };

    container.addEventListener("mouseenter", onEnter);
    container.addEventListener("mouseleave", onLeave);
    container.addEventListener("mousemove", onMove);
    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchend", onTouchEnd, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("resize", onResize);
    canvas.addEventListener("webglcontextlost", onContextLost, false);

    var resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(container);

    window.addEventListener("load", onResize);
    var t1 = setTimeout(onResize, 300);
    var t2 = setTimeout(onResize, 1200);

    var io = null;
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
      });
      io.observe(container);
    }

    function loadImage(src) {
      var image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = function () {
        imgRatio = image.naturalWidth / Math.max(1, image.naturalHeight);
        imageTexture = gl.createTexture();
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, imageTexture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
      };
      image.onerror = function () {
        degrade("image failed to load (CORS or bad URL): " + src);
        stop();
      };
      image.src = src;
    }

    function render() {
      rafId = requestAnimationFrame(render);
      if (!visible) return;

      var dt = 1 / 60;

      gl.bindBuffer(gl.ARRAY_BUFFER, quadVbo);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, quadEbo);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      gl.enableVertexAttribArray(0);

      if (pointer.moved) {
        pointer.moved = false;
        gl.useProgram(splatProgram.program);
        gl.uniform1i(splatProgram.uniforms.u_input_texture, velocity.read().attach(1));
        gl.uniform1f(
          splatProgram.uniforms.u_ratio,
          container.clientWidth / Math.max(1, container.clientHeight)
        );
        var uv = getPointerUV();
        gl.uniform2f(splatProgram.uniforms.u_point, uv.u, uv.v);
        gl.uniform3f(splatProgram.uniforms.u_point_value, pointer.dx, -pointer.dy, 0);
        var rr = params.cursorRadiusPx / Math.max(1, container.clientHeight);
        gl.uniform1f(splatProgram.uniforms.u_point_size, rr * rr);
        blit(velocity.write());
        velocity.swap();
        gl.uniform1i(splatProgram.uniforms.u_input_texture, outputColor.read().attach(1));
        gl.uniform3f(splatProgram.uniforms.u_point_value, params.cursorPower * 0.001, 0, 0);
        blit(outputColor.write());
        outputColor.swap();
      }

      gl.useProgram(divergenceProgram.program);
      gl.uniform2f(divergenceProgram.uniforms.u_texel, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(divergenceProgram.uniforms.u_velocity_texture, velocity.read().attach(1));
      blit(divergence);

      gl.useProgram(pressureProgram.program);
      gl.uniform2f(pressureProgram.uniforms.u_texel, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(pressureProgram.uniforms.u_divergence_texture, divergence.attach(1));
      for (var i = 0; i < 16; i++) {
        gl.uniform1i(pressureProgram.uniforms.u_pressure_texture, pressure.read().attach(2));
        blit(pressure.write());
        pressure.swap();
      }

      gl.useProgram(gradientSubtractProgram.program);
      gl.uniform2f(gradientSubtractProgram.uniforms.u_texel, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(gradientSubtractProgram.uniforms.u_pressure_texture, pressure.read().attach(1));
      gl.uniform1i(gradientSubtractProgram.uniforms.u_velocity_texture, velocity.read().attach(2));
      blit(velocity.write());
      velocity.swap();

      gl.useProgram(advectionProgram.program);
      gl.uniform2f(advectionProgram.uniforms.u_texel, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform2f(advectionProgram.uniforms.u_output_textel, velocity.texelSizeX, velocity.texelSizeY);
      gl.uniform1i(advectionProgram.uniforms.u_velocity_texture, velocity.read().attach(1));
      gl.uniform1i(advectionProgram.uniforms.u_input_texture, velocity.read().attach(1));
      gl.uniform1f(advectionProgram.uniforms.u_dt, dt);
      gl.uniform1f(advectionProgram.uniforms.u_dissipation, 0.97);
      blit(velocity.write());
      velocity.swap();

      gl.uniform2f(advectionProgram.uniforms.u_output_textel, outputColor.texelSizeX, outputColor.texelSizeY);
      gl.uniform1i(advectionProgram.uniforms.u_velocity_texture, velocity.read().attach(1));
      gl.uniform1i(advectionProgram.uniforms.u_input_texture, outputColor.read().attach(2));
      gl.uniform1f(advectionProgram.uniforms.u_dt, 8 * dt);
      gl.uniform1f(advectionProgram.uniforms.u_dissipation, 0.98);
      blit(outputColor.write());
      outputColor.swap();

      if (!imageTexture) return;

      gl.useProgram(displayProgram.program);
      gl.uniform1i(displayProgram.uniforms.u_velocity_texture, velocity.read().attach(2));
      gl.uniform1f(
        displayProgram.uniforms.u_ratio,
        container.clientWidth / Math.max(1, container.clientHeight)
      );
      gl.uniform1f(displayProgram.uniforms.u_img_ratio, imgRatio);
      gl.uniform1f(displayProgram.uniforms.u_disturb_power, params.distortionPower);
      gl.uniform1i(displayProgram.uniforms.u_output_texture, outputColor.read().attach(1));
      gl.uniform1f(displayProgram.uniforms.u_canvas_scale, 1);
      gl.uniform1f(displayProgram.uniforms.u_inner_scale, innerScale);
      gl.activeTexture(gl.TEXTURE0 + 3);
      gl.bindTexture(gl.TEXTURE_2D, imageTexture);
      gl.uniform1i(displayProgram.uniforms.u_text_texture, 3);

      blit(null);

      if (!revealed) {
        revealed = true;
        container.setAttribute("data-liquid-hover", "active");
        fallbackImg.style.opacity = "0";
      }
    }

    function getPointerUV() {
      var w = Math.max(1, container.clientWidth);
      var h = Math.max(1, container.clientHeight);
      return {
        u: pointer.x / w,
        v: 1 - pointer.y / h,
      };
    }

    function stop() {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
    }

    loadImage(imageSrc);
    render();

    return function () {
      stop();
      container.removeEventListener("mouseenter", onEnter);
      container.removeEventListener("mouseleave", onLeave);
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchend", onTouchEnd);
      container.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("load", onResize);
      clearTimeout(t1);
      clearTimeout(t2);
      canvas.removeEventListener("webglcontextlost", onContextLost);
      resizeObserver.disconnect();
      if (io) io.disconnect();
      disposeFBOs();
    };
  }

  customElements.define("liquid-hover", LiquidHover);
})();
